'use client'

import { Autocomplete as BaseAutocomplete } from '@base-ui/react/autocomplete'
import * as stylex from '@stylexjs/stylex'
import type { Ref } from 'react'

import { Input } from '../input'
import { breakpoints, colors, controlSizes, fonts, fontSizes, layers, radii, spacing } from '../tokens/tokens.stylex'

const styles = stylex.create({
  positioner: { zIndex: layers.popup },
  popup: {
    backgroundColor: colors.background,
    borderColor: colors.borderStrong,
    borderRadius: radii.small,
    borderStyle: 'solid',
    borderWidth: 1,
    boxSizing: 'border-box',
    color: colors.foreground,
    fontFamily: fonts.sans,
    fontSize: fontSizes.label,
    maxHeight: 'min(24rem, var(--available-height))',
    overflowY: 'auto',
    padding: spacing.space4,
    width: 'var(--anchor-width)',
  },
  label: { color: colors.muted, fontSize: fontSizes.caption, padding: spacing.space8 },
  empty: { color: colors.muted, padding: spacing.space12 },
  item: {
    alignItems: 'center',
    backgroundColor: { default: 'transparent', ':is([data-highlighted])': colors.selectedSurface },
    borderRadius: radii.small,
    cursor: 'pointer',
    display: 'flex',
    minHeight: { default: controlSizes.compact, [breakpoints.coarse]: controlSizes.touch },
    outline: 'none',
    paddingInline: spacing.space12,
  },
})

export type ComboboxOption<Value extends string = string> = { value: Value; label: string }

export type ComboboxGroup<Value extends string = string> = {
  value: string
  label?: string
  items: readonly ComboboxOption<Value>[]
}

export type ComboboxProps<Value extends string = string> = {
  'aria-label': string
  disabled?: boolean
  defaultOpen?: boolean
  emptyMessage?: string
  inputValue: string
  onInputValueChange(value: string): void
  onValueChange(value: Value): void
  options: readonly ComboboxGroup<Value>[]
  placeholder?: string
  ref?: Ref<HTMLInputElement>
  size?: 'md' | 'sm'
}

/**
 * Search and activate supplied options; use Select when the known choices need no text entry. Applications
 * own the query, filtering, option order, and activation result. Highlighting or typing alone does not
 * select an option.
 *
 * Status: supported.
 */
export function Combobox<Value extends string = string>({
  'aria-label': label,
  disabled = false,
  defaultOpen = false,
  emptyMessage = 'No options found',
  inputValue,
  onInputValueChange,
  onValueChange,
  options,
  placeholder,
  ref,
  size = 'md',
}: ComboboxProps<Value>) {
  return (
    <BaseAutocomplete.Root
      autoHighlight
      defaultOpen={defaultOpen}
      disabled={disabled}
      itemToStringValue={(item) => item.label}
      items={options}
      mode="none"
      onValueChange={onInputValueChange}
      openOnInputClick
      value={inputValue}
    >
      <BaseAutocomplete.Input render={<Input size={size} />} ref={ref} aria-label={label} placeholder={placeholder} />
      <BaseAutocomplete.Portal>
        <BaseAutocomplete.Positioner sideOffset={4} {...stylex.props(styles.positioner)}>
          <BaseAutocomplete.Popup {...stylex.props(styles.popup)}>
            <BaseAutocomplete.Empty {...stylex.props(styles.empty)}>{emptyMessage}</BaseAutocomplete.Empty>
            <BaseAutocomplete.List aria-label={label}>
              {(group: ComboboxGroup<Value>) => (
                <BaseAutocomplete.Group key={group.value} items={group.items}>
                  {group.label && (
                    <BaseAutocomplete.GroupLabel {...stylex.props(styles.label)}>
                      {group.label}
                    </BaseAutocomplete.GroupLabel>
                  )}
                  <BaseAutocomplete.Collection>
                    {(item: ComboboxOption<Value>) => (
                      <BaseAutocomplete.Item
                        key={item.value}
                        value={item}
                        onClick={() => onValueChange(item.value)}
                        {...stylex.props(styles.item)}
                      >
                        {item.label}
                      </BaseAutocomplete.Item>
                    )}
                  </BaseAutocomplete.Collection>
                </BaseAutocomplete.Group>
              )}
            </BaseAutocomplete.List>
          </BaseAutocomplete.Popup>
        </BaseAutocomplete.Positioner>
      </BaseAutocomplete.Portal>
    </BaseAutocomplete.Root>
  )
}
